import { useState } from 'react'

import { Collapse, Text } from '@mantine/core'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import dayjs from 'dayjs'

import { AuditChangeType } from 'src/graphql/types/expirationTrackers'

type AuditIconConfig = {
  icon: React.ReactNode
  iconClass: string
  bgClass: string
}

const auditTrailTypeConfig: Record<AuditChangeType, AuditIconConfig> = {
  [AuditChangeType.CREATE]: {
    icon: <Plus size={12} />,
    iconClass: 'text-doubleNickel-success-600',
    bgClass: 'bg-doubleNickel-success-50',
  },
  [AuditChangeType.UPDATE]: {
    icon: <Pencil size={12} />,
    iconClass: 'text-doubleNickel-brand-500',
    bgClass: 'bg-doubleNickel-brand-25',
  },
  [AuditChangeType.DELETE]: {
    icon: <Trash2 size={12} />,
    iconClass: 'text-doubleNickel-error-500',
    bgClass: 'bg-doubleNickel-error-50',
  },
}

// Audit titles that begin with "Failed" render with a red alert icon regardless
// of changeType. Backend convention: deviation/failure audits are authored as
// "Failed <action>" so the UI can surface them visually.
const failureIconConfig: AuditIconConfig = {
  icon: <AlertCircle size={12} />,
  iconClass: 'text-doubleNickel-error-500',
  bgClass: 'bg-doubleNickel-error-50',
}

const AuditTrailSection = ({ auditTrails }) => {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
          <Text size="xs" fw={400} className="uppercase text-inherit">
            AUDIT TRAIL ({auditTrails?.length || 0})
          </Text>
        </button>
      </div>

      <Collapse in={isExpanded}>
        {auditTrails?.length > 0 ? (
          <div className="relative pb-4 pl-4">
            <div className="absolute bottom-4 left-[11px] top-0 w-px bg-doubleNickel-gray-200" />

            <div className="flex flex-col gap-3">
              {auditTrails.map((entry) => {
                const isFailureAudit = entry.title?.startsWith('Failed')
                const config = isFailureAudit
                  ? failureIconConfig
                  : auditTrailTypeConfig[entry.changeType as AuditChangeType] ??
                    auditTrailTypeConfig[AuditChangeType.UPDATE]

                return (
                  <div
                    key={entry.auditTrailId}
                    className="relative flex gap-2.5"
                  >
                    <div
                      className={`relative z-10 -ml-4 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full ${config.bgClass}`}
                    >
                      <span className={config.iconClass}>{config.icon}</span>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
                      <div className="flex items-center justify-between">
                        <Text
                          size="xs"
                          fw={500}
                          className="text-doubleNickel-gray-900"
                        >
                          {entry.title}
                        </Text>
                      </div>
                      {entry.message && (
                        <Text size="xs" className="text-doubleNickel-gray-600">
                          {entry.message}
                        </Text>
                      )}
                      <Text size="xs" className="text-doubleNickel-gray-400">
                        {dayjs(entry.createdAt).format('MM/DD/YYYY h:mm A')}
                        {entry.employee
                          ? ` · ${entry.employee.firstName} ${entry.employee.lastName}`
                          : ' · System'}
                      </Text>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <Text
            size="xs"
            className="py-1 text-center text-doubleNickel-gray-400"
          >
            No audit trail entries
          </Text>
        )}
      </Collapse>
    </div>
  )
}

export default AuditTrailSection
